"use client";

import { c as _c } from "react/compiler-runtime";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import MarketplacePreview from "@/components/waves/MarketplacePreview";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useWaveSalesDecisions } from "@/hooks/waves/useWaveSalesDecisions";
import { useLayout } from "./layout/LayoutContext";
import React, { useCallback, useMemo } from "react";
interface MyStreamWaveSalesProps {
  readonly waveId: string;
}
const getFirstSaleUrl = (nftLinks: {
  readonly url_in_text?: string | null | undefined;
}[] | undefined): string | null => {
  for (const nftLink of nftLinks ?? []) {
    if (typeof nftLink.url_in_text !== "string") {
      continue;
    }
    const url = nftLink.url_in_text.trim();
    if (url.length > 0) {
      return url;
    }
  }
  return null;
};
const getSalesErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return null;
};
const MyStreamWaveSales: React.FC<MyStreamWaveSalesProps> = t0 => {
  const $ = _c(29);
  const {
    waveId
  } = t0;
  const {
    salesViewStyle
  } = useLayout();
  let t1;
  if ($[0] !== waveId) {
    t1 = {
      waveId
    };
    $[0] = waveId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    decisionPoints,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage
  } = useWaveSalesDecisions(t1);
  let t2;
  if ($[2] !== decisionPoints) {
    t2 = decisionPoints.slice().reverse().flatMap(_temp2);
    $[2] = decisionPoints;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const salesUrls = t2;
  let t3;
  if ($[4] !== fetchNextPage || $[5] !== hasNextPage || $[6] !== isFetching || $[7] !== isFetchingNextPage) {
    t3 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetching || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[4] = fetchNextPage;
    $[5] = hasNextPage;
    $[6] = isFetching;
    $[7] = isFetchingNextPage;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleIntersection = t3;
  const intersectionElementRef = useIntersectionObserver(handleIntersection);
  const isInitialLoading = isFetching && !isFetchingNextPage && decisionPoints.length === 0;
  let t4;
  if ($[9] !== error) {
    t4 = getSalesErrorMessage(error);
    $[9] = error;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const salesErrorMessage = t4;
  let salesContent;
  if (isInitialLoading) {
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6"><p className="tw-text-sm tw-font-medium tw-text-iron-200">Loading sales...</p></div>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    salesContent = t5;
  } else {
    if (isError) {
      const t5 = salesErrorMessage ? `: ${salesErrorMessage}` : ".";
      let t6;
      if ($[12] !== t5) {
        t6 = <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6"><p className="tw-text-sm tw-font-medium tw-text-iron-200">Failed to load sales{t5}</p></div>;
        $[12] = t5;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      salesContent = t6;
    } else {
      if (salesUrls.length === 0 && !hasNextPage) {
        let t5;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6"><p className="tw-text-sm tw-font-medium tw-text-iron-200">No sales yet.</p></div>;
          $[14] = t5;
        } else {
          t5 = $[14];
        }
        salesContent = t5;
      } else {
        let t5;
        if ($[15] !== salesUrls) {
          t5 = salesUrls.length > 0 ? <div data-testid="wave-sales-grid" className="tw-grid tw-gap-4 @lg:tw-grid-cols-2 @3xl:tw-grid-cols-3">{salesUrls.map(_temp3)}</div> : <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6"><p className="tw-text-sm tw-font-medium tw-text-iron-200">No sales yet.</p></div>;
          $[15] = salesUrls;
          $[16] = t5;
        } else {
          t5 = $[16];
        }
        let t6;
        if ($[17] !== isFetchingNextPage) {
          t6 = isFetchingNextPage && <div className="tw-mt-4"><WaveLeaderboardLoadingBar /></div>;
          $[17] = isFetchingNextPage;
          $[18] = t6;
        } else {
          t6 = $[18];
        }
        let t7;
        if ($[19] !== hasNextPage || $[20] !== intersectionElementRef) {
          t7 = hasNextPage && <div ref={intersectionElementRef} aria-hidden="true" className="tw-h-px" />;
          $[19] = hasNextPage;
          $[20] = intersectionElementRef;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        let t8;
        if ($[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
          t8 = <div className="tw-p-2 sm:tw-p-4 lg:tw-pr-2">{t5}{t6}{t7}</div>;
          $[22] = t5;
          $[23] = t6;
          $[24] = t7;
          $[25] = t8;
        } else {
          t8 = $[25];
        }
        salesContent = t8;
      }
    }
  }
  let t5;
  if ($[26] !== salesContent || $[27] !== salesViewStyle) {
    t5 = <div data-testid="wave-sales-scroll-container" className="tw-flex tw-w-full tw-flex-col tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 tw-@container hover:tw-scrollbar-thumb-iron-300" style={salesViewStyle}>{salesContent}</div>;
    $[26] = salesContent;
    $[27] = salesViewStyle;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
};
export default MyStreamWaveSales;
function _temp(winner) {
  const url = getFirstSaleUrl(winner.drop.nft_links);
  return url ? [url] : [];
}
function _temp2(decisionPoint) {
  return decisionPoint.winners.flatMap(_temp);
}
function _temp3(url_0, index) {
  return <MarketplacePreview key={`${url_0}-${index}`} href={url_0} compact={true} />;
}